import { environment } from './environment';

export interface StorefrontRuntimeConfig {
  schemaVersion: 2;
  application: 'storefront';
  publicUrl: string;
  environment: string;
  release: string;
  targetGeneration: true;
  access?: { enabled: boolean };
  api: {
    products: string;
    objects: string;
    persons: string;
    orders: string;
    finance: string;
    reviews: string;
    sign: string;
  };
  oidc: {
    issuer: string;
    profileApi: string;
    clientId: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
    scopes: string;
    responseType: string;
    browserProxy: { enabled: boolean; basePath: string };
  };
  realtime: { enabled: boolean; url: string };
  sentry: { enabled: boolean; dsn: string };
}

function record(value: unknown, field: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`Runtime config field ${field} must be an object`);
  }
  return value as Record<string, unknown>;
}

function text(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.trim() === '' || value.length > 512) {
    throw new Error(`Runtime config field ${field} must be a non-empty string`);
  }
  return value.trim();
}

function flag(value: unknown, field: string): boolean {
  if (typeof value !== 'boolean') {
    throw new Error(`Runtime config field ${field} must be a boolean`);
  }
  return value;
}

function absoluteURL(
  value: unknown,
  field: string,
  protocols: string[],
  allowUsername = false,
): string {
  const candidate = text(value, field);
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    throw new Error(`Runtime config field ${field} must be an absolute URL`);
  }
  if (
    !protocols.includes(parsed.protocol) ||
    parsed.password ||
    (!allowUsername && parsed.username)
  ) {
    throw new Error(`Runtime config field ${field} uses a forbidden URL`);
  }
  return candidate.replace(/\/$/, '');
}

function sameOriginPath(value: unknown, field: string): string {
  const candidate = text(value, field);
  if (
    !candidate.startsWith('/') ||
    candidate.startsWith('//') ||
    candidate.includes('?') ||
    candidate.includes('#')
  ) {
    throw new Error(`Runtime config field ${field} must be a same-origin path`);
  }
  return candidate.length > 1 ? candidate.replace(/\/$/, '') : candidate;
}

function oidcScopes(value: unknown): string {
  const candidate = text(value, 'oidc.scopes');
  const scopes = candidate.split(/\s+/);
  if (!scopes.includes('openid') || scopes.some((scope) => !/^[A-Za-z0-9._:-]+$/.test(scope))) {
    throw new Error('Runtime config OIDC scopes are invalid');
  }
  return scopes.join(' ');
}

function oidcResponseType(value: unknown): string {
  if (text(value, 'oidc.responseType') !== 'code') {
    throw new Error('Runtime config OIDC response type must be code');
  }
  return 'code';
}

export function parseStorefrontRuntimeConfig(
  value: unknown,
  expectedOrigin?: string,
): StorefrontRuntimeConfig {
  const root = record(value, 'root');
  if (root['schemaVersion'] !== 2 || root['application'] !== 'storefront') {
    throw new Error('Runtime config schema or application is unsupported');
  }
  const api = record(root['api'], 'api');
  const oidc = record(root['oidc'], 'oidc');
  const realtime = record(root['realtime'], 'realtime');
  const sentry = record(root['sentry'], 'sentry');
  const publicUrl = absoluteURL(root['publicUrl'], 'publicUrl', ['http:', 'https:']);
  if (expectedOrigin && new URL(publicUrl).origin !== new URL(expectedOrigin).origin) {
    throw new Error('Runtime config public URL must match the application origin');
  }
  if (root['targetGeneration'] !== true) {
    throw new Error('Runtime config must select the target generation');
  }
  const redirectUri = absoluteURL(oidc['redirectUri'], 'oidc.redirectUri', ['http:', 'https:']);
  const postLogoutRedirectUri = absoluteURL(
    oidc['postLogoutRedirectUri'],
    'oidc.postLogoutRedirectUri',
    ['http:', 'https:'],
  );
  if (
    new URL(redirectUri).origin !== new URL(publicUrl).origin ||
    new URL(postLogoutRedirectUri).origin !== new URL(publicUrl).origin
  ) {
    throw new Error('OIDC redirect URLs must use the Storefront public origin');
  }
  const runtimeEnvironment = text(root['environment'], 'environment');
  const release = text(root['release'], 'release');
  if (
    !/^[A-Za-z0-9._-]{1,128}$/.test(runtimeEnvironment) ||
    !/^[A-Za-z0-9._-]{1,128}$/.test(release)
  ) {
    throw new Error('Runtime config environment and release contain forbidden characters');
  }
  const realtimeEnabled = flag(realtime['enabled'], 'realtime.enabled');
  const sentryEnabled = flag(sentry['enabled'], 'sentry.enabled');
  const browserProxy = record(oidc['browserProxy'], 'oidc.browserProxy');
  const browserProxyEnabled = flag(browserProxy['enabled'], 'oidc.browserProxy.enabled');
  const browserProxyBasePath = browserProxy['basePath'];
  if (typeof browserProxyBasePath !== 'string') {
    throw new Error('Runtime config field oidc.browserProxy.basePath must be a string');
  }
  if (browserProxyEnabled && browserProxyBasePath.trim() === '') {
    throw new Error('Runtime config enabled OIDC browser proxy requires a base path');
  }
  if (!browserProxyEnabled && browserProxyBasePath.trim() !== '') {
    throw new Error('Runtime config disabled OIDC browser proxy must not define a base path');
  }
  return {
    schemaVersion: 2,
    application: 'storefront',
    publicUrl,
    environment: runtimeEnvironment,
    release,
    targetGeneration: true,
    ...(root['access'] === undefined
      ? {}
      : {
          access: { enabled: flag(record(root['access'], 'access')['enabled'], 'access.enabled') },
        }),
    api: {
      products: sameOriginPath(api['products'], 'api.products'),
      objects: sameOriginPath(api['objects'], 'api.objects'),
      persons: sameOriginPath(api['persons'], 'api.persons'),
      orders: sameOriginPath(api['orders'], 'api.orders'),
      finance: sameOriginPath(api['finance'], 'api.finance'),
      reviews: sameOriginPath(api['reviews'], 'api.reviews'),
      sign: sameOriginPath(api['sign'], 'api.sign'),
    },
    oidc: {
      issuer: absoluteURL(oidc['issuer'], 'oidc.issuer', ['https:']),
      profileApi: absoluteURL(oidc['profileApi'], 'oidc.profileApi', ['https:']),
      clientId: text(oidc['clientId'], 'oidc.clientId'),
      redirectUri,
      postLogoutRedirectUri,
      scopes: oidcScopes(oidc['scopes']),
      responseType: oidcResponseType(oidc['responseType']),
      browserProxy: {
        enabled: browserProxyEnabled,
        basePath: browserProxyEnabled
          ? sameOriginPath(browserProxyBasePath, 'oidc.browserProxy.basePath')
          : '',
      },
    },
    realtime: {
      enabled: realtimeEnabled,
      url: realtimeEnabled ? absoluteURL(realtime['url'], 'realtime.url', ['ws:', 'wss:']) : '',
    },
    sentry: {
      enabled: sentryEnabled,
      dsn: sentryEnabled ? absoluteURL(sentry['dsn'], 'sentry.dsn', ['https:'], true) : '',
    },
  };
}

export async function loadStorefrontRuntimeConfig(): Promise<StorefrontRuntimeConfig> {
  const url = new URL('runtime-config.json', document.baseURI);
  const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
  if (!response.ok) {
    throw new Error(`Runtime config request failed with status ${response.status}`);
  }
  const config = parseStorefrontRuntimeConfig(await response.json(), window.location.origin);
  Object.assign(environment, {
    runtimeConfigured: true,
    accessEnabled: config.access?.enabled ?? false,
    publicUrl: config.publicUrl,
    runtimeEnvironment: config.environment,
    release: config.release,
    version: config.release,
    productsV2Url: config.api.products,
    objectsV2Url: config.api.objects,
    personsV2Url: config.api.persons,
    ordersV2Url: config.api.orders,
    financeV2Url: config.api.finance,
    reviewsUrl: config.api.reviews,
    signUrl: config.api.sign,
    oidcAuthUrl: config.oidc.issuer,
    oidcProfileApi: config.oidc.profileApi,
    oidcClientId: config.oidc.clientId,
    oidcAuthScope: config.oidc.scopes,
    oidcResponseType: config.oidc.responseType,
    oidcRedirectUrl: config.oidc.redirectUri,
    oidcPostLogoutRedirectUri: config.oidc.postLogoutRedirectUri,
    oidcBrowserProxyEnabled: config.oidc.browserProxy.enabled,
    oidcBrowserProxyBasePath: config.oidc.browserProxy.basePath,
    realtimeEnabled: config.realtime.enabled,
    centrifugeUrl: config.realtime.url,
    sentryEnabled: config.sentry.enabled,
    sentry: config.sentry.dsn,
  });
  return config;
}
